import { Fragment, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { usePresence, useLiveChanges, useFollowedState } from '../live/live';
import { scrollToTop } from '../utils/scrollToTop';
import { PresenceAvatars, LiveCursors } from '../live/Presence';
import { LiveScope, LiveDraftView } from '../live/LiveField';
import { ShareProject } from '../components/ShareProject';
import { CollabButton } from '../live/Collab';
import { taskStatusName } from '../utils/taskStatusName';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, ChevronDown, Check, Lock, X, Plus, Link as LinkIcon, Users, LayoutGrid, Settings } from 'lucide-react';
import * as Select from '@radix-ui/react-select';
import { useRole } from '../context/RoleContext';
import { useUnsavedChangesGuard } from '../context/NavigationGuardContext';
import { api } from '../api/client';
import { ConfirmModal } from '../components/ConfirmModal';
import type { Project, GridColumn, GridRow, PlannerTask, ProjectEvent } from '../types';
import type { StageChecklistItem } from '../components/ProjectForm';
import { milestoneHealth, HEALTH_TITLE_KEY, HEALTH_BADGE_CLASS } from '../utils/executionHealth';
import { ProjectTimeline } from '../components/ProjectTimeline';
import { StrategicPanel, SequencingPanel } from '../components/PrequalificationPanels';
import { MilestonesEditor, MilestoneCard, newMilestone } from '../components/MilestonesEditor';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { DeliverablesEditor } from '../components/DeliverablesEditor';
import { lifecycleRank, RANK, PROJECT_TOPICS, TOPIC_UNLOCK_RANK, LIFECYCLE_STAGES, STAGES_WITH_VIEW, topicFromParam, type ProjectTopic } from '../utils/stages';
import { ProjectStageBar, StageNext, useStageName } from '../components/ProjectStageBar';
import { ProjectSynthesis } from '../components/ProjectSynthesis';
import { ProjectSection, StepBadge, type StepMark } from '../components/ProjectSection';
import { FieldHistory, fieldChanges } from '../components/FieldHistory';
import { projectDisplayCode } from '../utils/identifiers';
import { defaultProjectRole } from '../proposals/roles-projet/projectRoles';
import { ProjectRoleSelect, ProjectRolesHeader } from '../proposals/roles-projet/ProjectRoleSelect';
import { VueJalonsTaches } from '../proposals/vue-jalons-taches/VueJalonsTaches';
import { DocumentsSummary } from '../proposals/documents/DocumentsSummary';
import { ProjectDocumentsRegister } from '../proposals/documents/DocumentsRegister';
import {
  FormState, type Milestone, EMPTY_FORM, VALUE_ADDED_OPTIONS, EFFORT_OPTIONS, TIMELINE_OPTIONS,
  projectToForm, formToPayload, getStageChecklist, parseJSON,
  BareTextarea, AutosuggestInput, TagInput,
  CollaboratorsSection,
} from '../components/ProjectForm';
import { useT } from '../i18n';
import { ProjectTeamRolesPanel } from '../proposals/roles-projet/ProjectTeamRoles';

const HOURS_PER_WEEK = 40;

const URGENCY_LABEL_KEY: Record<string, string> = {
  Low: 'projectDetail.scoping.urgencyLow',
  Medium: 'projectDetail.scoping.urgencyMedium',
  High: 'projectDetail.scoping.urgencyHigh',
};

const INTERVENTION_LABEL_KEY: Record<string, string> = {
  Weekly: 'projectDetail.scoping.interventionWeekly',
  'Bi-weekly': 'projectDetail.scoping.interventionBiWeekly',
  Monthly: 'projectDetail.scoping.interventionMonthly',
};

// Calendar weeks spanned by an inclusive YYYY-MM → YYYY-MM time horizon.
function weeksBetween(startMonth: string, endMonth: string): number {
  if (!startMonth || !endMonth) return 0;
  const [sy, sm] = startMonth.split('-').map(Number);
  const [ey, em] = endMonth.split('-').map(Number);
  if (!sy || !sm || !ey || !em) return 0;
  const start = new Date(sy, sm - 1, 1);
  const end = new Date(ey, em, 0); // day 0 of the next month = last day of end month
  const days = (end.getTime() - start.getTime()) / 86400000 + 1;
  return days > 0 ? days / 7 : 0;
}

// Order phases for triage (lower = earlier in the lifecycle).
const PHASE_ORDER = ['Inbox', 'Préqualification', 'Scoping', 'Qualification', 'Planning', 'Execution', 'Handover', 'Closed', 'Backlog', 'Deferred'];

// Per-target confirmation copy for the generic "advance to a named stage" modal.
function advanceMessage(t: (key: string, params?: Record<string, string | number>) => string, target: string | null, stageName: (label: string) => string): string {
  if (!target) return '';
  switch (target) {
    case 'Qualification': return t('projectDetail.advanceMsg.qualification');
    case 'Execution': return t('projectDetail.advanceMsg.execution');
    case 'Handover': return t('projectDetail.advanceMsg.handover');
    default: return t('projectDetail.advanceMsg.generic', { target: stageName(target) });
  }
}

// This person's allocations on OTHER projects, triaged by phase, with a capacity total.
function PersonAllocations({ name, projects, excludeId }: { name: string; projects: Project[]; excludeId: number }) {
  const { t } = useT();
  const n = name.trim().toLowerCase();
  const rows: { project: string; phase: string; role: string; pct: number; hours: number }[] = [];
  for (const p of projects) {
    if (p.id === excludeId) continue;
    const scop = parseJSON<any>(p.scoping, {});
    const allocs = Array.isArray(scop.allocations) ? scop.allocations : [];
    for (const a of allocs) {
      if ((a.name || '').trim().toLowerCase() !== n) continue;
      const pct = parseFloat(a.allocation_pct) || 0;
      const hours = Math.round(40 * pct / 100 * weeksBetween(scop.time_horizon_start, scop.time_horizon_end));
      rows.push({ project: p.name, phase: p.status_label || '—', role: a.role || '—', pct, hours });
    }
  }
  rows.sort((a, b) => (PHASE_ORDER.indexOf(a.phase) - PHASE_ORDER.indexOf(b.phase)) || a.project.localeCompare(b.project));
  const totalPct = rows.reduce((s, r) => s + r.pct, 0);
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  return (
    <div className="alloc-expand">
      {rows.length === 0 ? (
        <div className="alloc-expand-empty">{t('projectDetail.alloc.notAllocatedElsewhere')}</div>
      ) : (
        <>
          <div className="alloc-expand-head"><span>{t('projectDetail.alloc.colProject')}</span><span>{t('projectDetail.alloc.colPhase')}</span><span>{t('projectDetail.alloc.colRole')}</span><span>%</span><span>{t('projectDetail.alloc.colHours')}</span></div>
          {rows.map((r, i) => (
            <div key={i} className="alloc-expand-row">
              <span className="alloc-expand-proj" title={r.project}>{r.project}</span>
              <span><span className="badge">{r.phase}</span></span>
              <span className="alloc-expand-role">{r.role}</span>
              <span className="alloc-num">{r.pct}%</span>
              <span className="alloc-num">{r.hours || '—'}</span>
            </div>
          ))}
          <div className="alloc-expand-total"><span>{t('projectDetail.alloc.committedElsewhere')}</span><span className="alloc-num">{totalPct}% · {totalHours} h</span></div>
        </>
      )}
    </div>
  );
}

// One block of the settings dialog: a label row with its action, then the content.
function SettingsBlock({ title, actions, children }: { title: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="pd-setting">
      <div className="pd-setting-head">
        <span className="pd-setting-title">{title}</span>
        {actions}
      </div>
      {children}
    </div>
  );
}

// ── Tasks quick-access panel (shown on the project outline) ──

type LinkedProjectRef = { id: number; name: string; project_code: string | null };
type PanelTask = { id: number; title: string; column_label: string; project_id: number; project_name: string; project_code: string | null; linked_projects: LinkedProjectRef[] };

function ProjectTasksPanel({ projectId, projectName, projectCode, allProjects }: { projectId: number; projectName: string; projectCode: string | null; allProjects: Project[] }) {
  const { t } = useT();
  const [tasks, setTasks] = useState<PanelTask[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Followed in collaborator mode (the others see the same list and task open).
  const [showAllTasks, setShowAllTasks] = useFollowedState(`project:${projectId}:tasksAll`, false);
  const [openTaskRef, setOpenTaskRef] = useFollowedState<{ id: number; title: string } | null>(`project:${projectId}:taskOpen`, null);

  // include_linked=true also surfaces tasks whose *second* project is this
  // one — they belong here too, but read-only unless this is their home
  // project (see TaskDetailModal's isSource gate).
  const loadTasks = useCallback(() => {
    return api.getProjectTasks(projectId, true)
      .then((t) => setTasks(t as PanelTask[]))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [projectId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // Collapse to the first ~4 cards (with a bottom fade) until the user expands.
  const capped = !showAllTasks && tasks.length > 4;

  // Opens the task in an editable overlay in place, instead of navigating away
  // to the full task board. Planner-only fields (due/planned dates, assignee,
  // notes) get filled in by TaskDetailModal itself via getTaskDetail — as is
  // its authoritative project_title/project_code, since a task reached here
  // via its *linked* project has a different home project than this page.
  const openTaskDetail = (t: { id: number; title: string }) => setOpenTaskRef({ id: t.id, title: t.title });
  const openTask: PlannerTask | null = openTaskRef && ({
    id: openTaskRef.id,
    source: 'project',
    title: openTaskRef.title,
    description: null,
    due_date: null,
    planned_at: null,
    planned_minutes: 60,
    project_id: projectId,
    project_code: projectCode,
    project_title: projectName,
    board_id: null,
    board_name: null,
    assignee_name: null,
    column_key: null,
  });

  return (
    <div className="detail-section" style={{ marginBottom: 'var(--s16)' }}>
      <div className="detail-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--s8)' }}>
        <span>{loaded ? t('projectDetail.tasksPanel.headingCount', { n: tasks.length }) : t('projectDetail.tasksPanel.heading')}</span>
        <a className="btn btn-ghost btn-sm" href={`#tasks?project=${projectId}`}>{t('projectDetail.tasksPanel.boardLink')}</a>
      </div>
      {!loaded ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('common.loading')}</div>
      ) : tasks.length === 0 ? (
        <div className="text-ink-3" style={{ fontSize: 'var(--t-12)' }}>{t('projectDetail.tasksPanel.none')}</div>
      ) : (
        <>
          <div className={`panel-tasks-list${capped ? ' panel-tasks-capped' : ''}`}>
            {tasks.map((task) => {
              // Editable only from the task's home project — reached here via
              // a linked project, it's a read-only copy (see TaskDetailModal).
              const isSource = task.project_id === projectId;
              const otherProjects = task.linked_projects.filter((p) => p.id !== projectId);
              return (
                <button
                  key={task.id}
                  type="button"
                  className="cal-rail-task"
                  style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit' }}
                  onClick={() => openTaskDetail(task)}
                >
                  <div className="cal-rail-task-main">
                    <span className="cal-rail-task-title">{task.title}</span>
                  </div>
                  <span className="cal-rail-task-due">{taskStatusName(task.column_label, t)}</span>
                  {!isSource && (
                    <div className="task-card-linked" title={t('tasks.readOnlyNotice')}>
                      <LinkIcon size={11} />
                      {t('tasks.primaryProjectPrefix')} {(task.project_code || task.project_name).toUpperCase()}
                    </div>
                  )}
                  {isSource && otherProjects.length > 0 && (
                    <div className="task-card-linked" title={t('tasks.linkedProject')}>
                      <LinkIcon size={11} />
                      {t('tasks.presentInProjects', { projects: otherProjects.map((p) => (p.project_code || p.name).toUpperCase()).join(', ') })}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {capped && (
            <button
              className="btn btn-ghost btn-sm panel-tasks-expand"
              type="button"
              onClick={() => setShowAllTasks(true)}
            >{t('projectDetail.tasksPanel.showAll', { n: tasks.length })}</button>
          )}
        </>
      )}
      {openTask && (
        <TaskDetailModal
          task={openTask}
          contextProjectId={projectId}
          projects={allProjects}
          onClose={() => setOpenTaskRef(null)}
          onSaved={() => { setOpenTaskRef(null); loadTasks(); }}
        />
      )}
    </div>
  );
}

export function ProjectDetailPage() {
  const { t } = useT();
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.split('?')[1] || '');
  const projectId = Number(params.get('id'));
  const stageName = useStageName();
  const { isAdmin, isDirection, canSeeProposals } = useRole();
  const { user } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [pipelineColumns, setPipelineColumns] = useState<GridColumn[]>([]);
  const [portfolioDomains, setPortfolioDomains] = useState<GridColumn[]>([]);
  const [levels, setLevels] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM });
  // Snapshot of the form as last loaded/saved — diffed against `form` to
  // detect unsaved edits for the leave-without-saving prompt.
  const [savedForm, setSavedForm] = useState<FormState>({ ...EMPTY_FORM });
  // Collaborator mode: what is shown here follows the presenter (useFollowedState).
  const [activeTopic, setActiveTopic] = useFollowedState<ProjectTopic>(`project:${projectId}:topic`, 'apercu');
  // The stage shown in the "À faire" tab when it isn't the current one (a
  // stage clicked in the header); null = the project's current stage.
  const [viewedStage, setViewedStage] = useFollowedState<string | null>(`project:${projectId}:stage`, null);
  // Plan & exécution, once in Execution: follow-up (milestones/tasks view) or the plan editor.
  const [planView, setPlanView] = useFollowedState<'suivi' | 'plan'>(`project:${projectId}:planView`, 'suivi');
  // [Proposition: roles-projet] "Équipe et rôles" panel.
  const [showTeamRoles, setShowTeamRoles] = useFollowedState(`project:${projectId}:teamRoles`, false);
  const [submitFeedback, setSubmitFeedback] = useState<string | null>(null);
  // Tracked separately from the message text itself so the banner style doesn't
  // depend on sniffing English substrings out of a (now-translatable) message.
  const [submitFeedbackOk, setSubmitFeedbackOk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [decideTarget, setDecideTarget] = useState<null | 'advance' | 'defer'>(null);
  const [advanceTarget, setAdvanceTarget] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [statusTarget, setStatusTarget] = useState<string | null>(null);
  const [allocAddName, setAllocAddName] = useState('');
  const [expandedAlloc, setExpandedAlloc] = useFollowedState<number | null>(`project:${projectId}:alloc`, null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  // PMO accompanist choices = the admin accounts.
  const [pmoAccompanists, setPmoAccompanists] = useState<string[]>([]);
  useEffect(() => { api.getPmoAccompanists().then(setPmoAccompanists).catch(() => {}); }, []);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedReason, setBlockedReason] = useState('');
  const [blockedReasonDraft, setBlockedReasonDraft] = useState('');
  const [stageLinkCopied, setStageLinkCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Deep link into a topic tab, e.g. #project?id=12&tab=plan — shared from a
  // notification or copied via the header's link button. Old stage-tab links
  // (tab=execution, tab=scoping…) open the topic that now holds that content.
  const linkedTopic = topicFromParam(params.get('tab'));

  const canEdit = isAdmin || isDirection;
  const isChangeRequestMode = !isAdmin && isDirection;

  // Sidebar "Projets récents": remember this project once it's open (not on every reload).
  const openedId = project && (project.access_level ?? 'full') !== 'card' ? project.id : null;
  useEffect(() => {
    if (openedId == null) return;
    api.recordRecentProject(openedId)
      .then(() => window.dispatchEvent(new Event('pmo:recent-projects')))
      .catch(() => { /* not critical */ });
  }, [openedId]);

  // `nextTopic` lets callers pick the tab to land on after the reload: the
  // initial mount opens on the URL's tab (if any) or the Aperçu; a save or a
  // stage change stays where the user is.
  const loadData = useCallback(async (nextTopic?: ProjectTopic | 'auto') => {
    const [projects, pipeCols, domainCols, rows] = await Promise.all([
      api.getProjects() as Promise<Project[]>,
      api.getGridColumns('pipeline') as Promise<GridColumn[]>,
      api.getGridColumns('portfolio') as Promise<GridColumn[]>,
      api.getGridRows() as Promise<GridRow[]>,
    ]);
    setPipelineColumns(pipeCols);
    setPortfolioDomains(domainCols);
    setLevels(rows);
    setAllProjects(projects);
    const found = projects.find(p => p.id === projectId);
    setProject(found || null);
    if (found) {
      const f = projectToForm(found);
      setForm(f);
      setSavedForm(f);
      setIsPrivate(found.is_private ?? false);
      setIsBlocked(found.is_blocked ?? false);
      setBlockedReason(found.blocked_reason ?? '');
      setBlockedReasonDraft('');
      if (nextTopic === 'auto') {
        // Where the work is: the stage's "À faire" view while a stage asks for
        // something, the follow-up once the project runs, else the Aperçu.
        const label = pipeCols.find(c => c.id === found.status_id)?.label || '';
        setActiveTopic(label === 'Execution' ? 'plan'
          : (STAGES_WITH_VIEW as readonly string[]).includes(label) && (found.access_level ?? 'full') === 'full' ? 'etape'
          : 'apercu');
      } else if (nextTopic) setActiveTopic(nextTopic);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(linkedTopic || 'auto'); }, [loadData]);

  // Where the project stands: its saved stage (stage changes always save).
  const currentStageLabel = pipelineColumns.find(c => c.id === (project?.status_id ?? form.statusId))?.label || '';
  const rank = lifecycleRank(currentStageLabel, project?.qualified_at);
  const scopingOpen = rank >= RANK.scoping;

  // [Proposition: vue-jalons-taches] After a change made in the milestones/tasks
  // view: take the milestones' new statuses into the form without touching other
  // unsaved edits — a later Save must not undo them. A stage change (project
  // delivered → Handover) reloads the page data.
  const refreshProjectMilestones = useCallback(async () => {
    const projects = await api.getProjects() as Project[];
    const found = projects.find(p => p.id === projectId);
    if (!found) return;
    if (found.status_id !== project?.status_id) { await loadData(); return; }
    const milestones = projectToForm(found).milestones;
    setForm(f => ({ ...f, milestones }));
    setSavedForm(f => ({ ...f, milestones }));
    setProject(found);
  }, [projectId, project?.status_id, loadData]);

  // The milestones/tasks view and the project timeline show each other's changes
  // (a milestone cancelled there, restored here) without a page reload.
  const [timelineKey, setTimelineKey] = useState(0);
  // The project's events, for the cards' field history; reloaded after every save.
  const [projectEvents, setProjectEvents] = useState<ProjectEvent[]>([]);
  useEffect(() => {
    if (!project) return;
    api.getProjectTimeline(project.id)
      .then((r: any) => setProjectEvents(Array.isArray(r?.projectEvents) ? r.projectEvents : []))
      .catch(() => {});
  }, [project, timelineKey]);
  const [executionKey, setExecutionKey] = useState(0);
  // [Proposition: documents] A document added or deleted in the side panel or the
  // register: both lists, the follow-up view's register and the history (it logs it).
  const [docsKey, setDocsKey] = useState(0);
  const refreshAfterDocuments = useCallback(() => {
    setDocsKey(k => k + 1);
    setTimelineKey(k => k + 1);
    setExecutionKey(k => k + 1);
  }, []);
  const refreshAfterExecutionView = useCallback(async () => {
    setTimelineKey(k => k + 1);
    await refreshProjectMilestones();
  }, [refreshProjectMilestones]);
  const refreshAfterTimeline = useCallback(async () => {
    setExecutionKey(k => k + 1);
    await refreshProjectMilestones();
  }, [refreshProjectMilestones]);

  const isDirty = canEdit && JSON.stringify(form) !== JSON.stringify(savedForm);
  useUnsavedChangesGuard(isDirty);

  // Live: who else is on this project, and someone else's saves. The milestones
  // view and the timeline just refetch; the form reloads too unless this user
  // has unsaved edits — then a banner offers to reload (dropping them).
  const livePeople = usePresence(`project:${projectId}`, user?.id);
  const [remoteChange, setRemoteChange] = useState<{ actor: string | null } | null>(null);
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  useLiveChanges((c) => {
    if (c.area === 'kanban' || (c.area === 'project' && c.projectId && c.projectId !== projectId)) return;
    setExecutionKey(k => k + 1);
    setTimelineKey(k => k + 1);
    if (isDirtyRef.current) setRemoteChange({ actor: c.actor ?? null });
    else loadData();
  });

  // Keep the URL's tab param in sync with the open topic (via replaceState, so
  // it doesn't fire hashchange/the unsaved-changes guard) — the address bar is
  // then always a direct, shareable link back to the topic in view.
  useEffect(() => {
    if (!project) return;
    const current = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (current.get('tab') === activeTopic) return;
    current.set('tab', activeTopic);
    window.history.replaceState(null, '', `#project?${current.toString()}`);
  }, [activeTopic, project]);

  // The allocation table (Équipe, from Cadrage on) lists every intake
  // collaborator: add any that are missing.
  const allocationsShown = (activeTopic === 'equipe' || (activeTopic === 'etape' && (viewedStage ?? currentStageLabel) === 'Scoping')) && scopingOpen;
  useEffect(() => {
    if (!allocationsShown) return;
    const have = new Set(form.allocations.map(a => a.name.trim().toLowerCase()).filter(Boolean));
    const missing = form.collaborators.filter(c => c.name.trim() && !have.has(c.name.trim().toLowerCase()));
    if (missing.length === 0) return;
    setForm(f => ({ ...f, allocations: [...f.allocations, ...missing.map(c => ({ name: c.name, role: '', allocationPct: '', projectRole: defaultProjectRole(c.name, f.sponsor) }))] }));
  }, [allocationsShown, form.collaborators, form.allocations]);

  // Total hours = Σ (40h × allocation% × weeks of the time horizon). Persist it.
  useEffect(() => {
    if (!allocationsShown) return;
    const weeks = weeksBetween(form.timeHorizonStart, form.timeHorizonEnd);
    const total = Math.round(form.allocations.reduce((s, a) => s + (HOURS_PER_WEEK * (parseFloat(a.allocationPct) || 0) / 100) * weeks, 0));
    if (String(total) !== form.totalHours) setForm(f => ({ ...f, totalHours: String(total) }));
  }, [allocationsShown, form.allocations, form.timeHorizonStart, form.timeHorizonEnd, form.totalHours]);

  const goBack = () => { history.back(); };

  // Équipe et moyens, from Cadrage on: Save waits until the time horizon is set
  // and every listed person has a non-zero time allocation. Keyed off the open
  // tab, so a project that went past Cadrage with these still missing can save
  // its other topics.
  const scopingIssues = (f: FormState): string[] => {
    if (!allocationsShown) return [];
    const issues: string[] = [];
    if (!f.timeHorizonStart && !f.timeHorizonEnd) issues.push(t('projectDetail.scoping.saveNeedsBothDates'));
    else if (!f.timeHorizonStart) issues.push(t('projectDetail.scoping.saveNeedsStartDate'));
    else if (!f.timeHorizonEnd) issues.push(t('projectDetail.scoping.saveNeedsEndDate'));
    if (f.allocations.length === 0) issues.push(t('projectDetail.scoping.saveNeedsPeople'));
    else if (f.allocations.some(a => !((parseFloat(a.allocationPct) || 0) > 0))) issues.push(t('projectDetail.scoping.saveNeedsAllAllocations'));
    return issues;
  };

  // Saves the given form (the page's, or one with a milestone added from the follow-up view).
  const saveForm = async (f: FormState) => {
    if (!f.name.trim() || !project) return;
    // Mirror of the render-time save gate — the button is disabled for these,
    // this just keeps a stray call from slipping past.
    if (scopingIssues(f).length > 0) return;
    const payload = formToPayload(f);

    if (isChangeRequestMode) {
      try {
        await api.createChangeRequest(project.id, payload);
        setSubmitFeedback(t('projectDetail.changeRequestSuccess'));
        setSubmitFeedbackOk(true);
        setTimeout(() => goBack(), 2000);
      } catch (err: any) {
        setSubmitFeedback(err.message || t('projectDetail.changeRequestFailed'));
        setSubmitFeedbackOk(false);
      }
      return;
    }

    await api.updateProject(project.id, payload);
    if (f.sponsor.trim()) api.createPerson(f.sponsor.trim()).catch(() => {});
    for (const ug of f.userGroups) api.createUserGroup(ug).catch(() => {});
    for (const c of f.collaborators) api.createPerson(c.name).catch(() => {});
    for (const infra of f.infrastructure) api.createInfrastructure(infra).catch(() => {});
    loadData();
  };
  const handleSave = () => saveForm(form);

  // Adding a milestone from the follow-up view: the plan's milestone card in a
  // dialog, saved like the plan editor (change request for direction).
  const [addingMilestone, setAddingMilestone] = useState<Milestone | null>(null);
  const saveAddedMilestone = async () => {
    if (!addingMilestone) return;
    const next = { ...form, milestones: [...form.milestones, addingMilestone] };
    setAddingMilestone(null);
    setForm(next);
    await saveForm(next);
    setExecutionKey(k => k + 1);
    setTimelineKey(k => k + 1);
  };

  // Préqualification verdict: saves current work AND moves the project's stage,
  // in one explicit, confirmed action (advance → Scoping, defer → Deferred).
  const applyDecision = async (verdict: 'advance' | 'defer') => {
    if (!project) return;
    const targetLabel = verdict === 'advance' ? 'Scoping' : 'Deferred';
    const targetId = pipelineColumns.find(c => c.label === targetLabel)?.id ?? null;
    const next: FormState = { ...form, prequalVerdict: verdict, ...(targetId ? { statusId: targetId } : {}) };
    setForm(next);
    setDecideTarget(null);
    await api.updateProject(project.id, formToPayload(next));
    loadData();
  };

  // "Advance to a named stage" with a confirm: saves the current work and
  // moves the stage in one call.
  const doAdvanceTo = async (label: string) => {
    if (!project) return;
    const targetId = pipelineColumns.find(c => c.label === label)?.id ?? null;
    const next: FormState = { ...form, ...(targetId ? { statusId: targetId } : {}) };
    setForm(next);
    setAdvanceTarget(null);
    await api.updateProject(project.id, formToPayload(next));
    loadData();
  };

  // ── Allocations (kept in sync with the intake collaborators) ──
  const updateAllocation = (idx: number, patch: Partial<{ role: string; allocationPct: string; projectRole: string }>) =>
    setForm({ ...form, allocations: form.allocations.map((x, i) => i === idx ? { ...x, ...patch } : x) });

  // Removing an allocation also removes that person from the intake collaborators.
  const removeAllocation = (idx: number) => {
    const nm = form.allocations[idx]?.name?.trim().toLowerCase();
    setForm({
      ...form,
      allocations: form.allocations.filter((_, i) => i !== idx),
      collaborators: nm ? form.collaborators.filter(c => c.name.trim().toLowerCase() !== nm) : form.collaborators,
    });
  };

  // Adding a person here also adds them to the intake collaborators.
  const addAllocationPerson = (name: string) => {
    const n = name.trim();
    setAllocAddName('');
    if (!n || form.allocations.some(a => a.name.trim().toLowerCase() === n.toLowerCase())) return;
    setForm({
      ...form,
      allocations: [...form.allocations, { name: n, role: '', allocationPct: '', projectRole: defaultProjectRole(n, form.sponsor) }],
      collaborators: form.collaborators.some(c => c.name.trim().toLowerCase() === n.toLowerCase())
        ? form.collaborators
        : [...form.collaborators, { name: n, allocation: 0 }],
    });
  };

  const allocWeeks = weeksBetween(form.timeHorizonStart, form.timeHorizonEnd);

  // ── Clôture: record the achieved score on a strategic resource (nested or standalone) ──
  const setAchieved = (capIdx: number | null, resIdx: number, achieved: number | null) => {
    if (!form.rcajo) return;
    if (capIdx === null) {
      setForm({ ...form, rcajo: { ...form.rcajo, resources: (form.rcajo.resources ?? []).map((r, i) => i === resIdx ? { ...r, achieved_score: achieved } : r) } });
    } else {
      setForm({ ...form, rcajo: { ...form.rcajo, capabilities: form.rcajo.capabilities.map((c, ci) => ci === capIdx ? { ...c, resources: c.resources.map((r, i) => i === resIdx ? { ...r, achieved_score: achieved } : r) } : c) } });
    }
  };

  // Push achieved scores to RCAJO (PMO-scores bucket) and close the project.
  const recordAndClose = async () => {
    if (!project) return;
    const scores: { resource_id: number; capability_id: number | null; score: number }[] = [];
    if (form.rcajo) {
      for (const c of form.rcajo.capabilities) {
        for (const r of c.resources) {
          if (r.resource_id != null) scores.push({ resource_id: r.resource_id, capability_id: c.capability_id, score: r.achieved_score ?? r.target_score });
        }
      }
      for (const r of (form.rcajo.resources ?? [])) {
        if (r.resource_id != null) scores.push({ resource_id: r.resource_id, capability_id: null, score: r.achieved_score ?? r.target_score });
      }
    }
    const closedId = pipelineColumns.find(c => c.label === 'Closed')?.id ?? null;
    const next: FormState = { ...form, ...(closedId ? { statusId: closedId } : {}) };
    setForm(next);
    setConfirmClose(false);
    await api.updateProject(project.id, formToPayload(next));
    if (scores.length) { try { await api.recordRcajoPmoScores({ project_id: project.id, scores }); } catch { /* tolerate; project still closes */ } }
    loadData();
  };

  const handleDelete = () => {
    if (!project) return;
    setConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!project) return;
    setConfirmDelete(false);
    await api.deleteProject(project.id);
    goBack();
  };

  const toggleValueAdded = (val: string) => {
    if (!canEdit) return;
    setForm({
      ...form,
      valueAdded: form.valueAdded.includes(val)
        ? form.valueAdded.filter(v => v !== val)
        : [...form.valueAdded, val],
    });
  };

  if (loading) {
    return <div className="page-loading"><div className="spinner" /></div>;
  }

  if (!project) {
    return (
      <div style={{ padding: 'var(--s24)' }}>
        <button className="btn btn-ghost btn-sm" onClick={goBack}>
          <ArrowLeft size={14} style={{ verticalAlign: -2 }} /> {t('common.back')}
        </button>
        <div style={{ marginTop: 'var(--s16)', color: 'var(--ink-3)' }}>{t('projectDetail.notFound')}</div>
      </div>
    );
  }

  // A private project this user isn't part of can't be opened: only its card shows in the lists.
  if (project.access_level === 'card') {
    return (
      <div style={{ padding: 'var(--s24)' }}>
        <button className="btn btn-ghost btn-sm" onClick={goBack}>
          <ArrowLeft size={14} style={{ verticalAlign: -2 }} /> {t('common.back')}
        </button>
        <div style={{ marginTop: 'var(--s16)', color: 'var(--ink-3)' }}>
          <Lock size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{t('projectDetail.privateNotMember', { name: project.name })}
        </div>
      </div>
    );
  }
  // A public project this user isn't part of: its summary, content and team
  // only, without the strategy, plan and closing topics nor the side panel
  // (tasks, documents, history).
  const overviewOnly = project.access_level === 'overview';

  // The "À faire" tab comes first while the current stage has work to show,
  // or while another stage was opened from the header.
  const hasStageTab = !overviewOnly && (viewedStage != null || (STAGES_WITH_VIEW as readonly string[]).includes(currentStageLabel));
  const topics: ProjectTopic[] = overviewOnly ? ['apercu', 'contenu', 'equipe'] : [...(hasStageTab ? ['etape' as const] : []), ...PROJECT_TOPICS];
  const currentTopic: ProjectTopic = topics.includes(activeTopic) ? activeTopic : 'apercu';
  const topicOpen = (topic: ProjectTopic) => rank >= TOPIC_UNLOCK_RANK[topic];
  const UNLOCK_STAGE: Record<ProjectTopic, string> = {
    etape: 'Inbox', apercu: 'Inbox', contenu: 'Inbox', equipe: 'Inbox', strategie: 'Préqualification', plan: 'Planning', cloture: 'Handover',
  };

  // [Proposition: documents] "See all documents" from the side panel: open the
  // register once it has loaded — in the follow-up view once the project runs,
  // at the bottom of Aperçu before (there is no follow-up view yet).
  const hasFollowUp = !overviewOnly && rank >= RANK.execution;
  const showAllDocuments = () => {
    if (hasFollowUp) {
      setActiveTopic('plan');
      setPlanView('suivi');
    } else {
      setViewedStage(null);
      setActiveTopic('apercu');
    }
    let tries = 0;
    const scroll = () => {
      const el = document.getElementById('docs-register');
      if (el) scrollToTop(el);
      else if (++tries < 30) setTimeout(scroll, 100);
    };
    setTimeout(scroll, 50);
  };

  const copyStageLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#project?id=${projectId}&tab=${currentTopic}`;
    try {
      await navigator.clipboard.writeText(url);
      setStageLinkCopied(true);
      setTimeout(() => setStageLinkCopied(false), 1500);
    } catch {
      // Clipboard API unavailable/denied — the URL is already in the address bar.
    }
  };

  const checklist = getStageChecklist(currentStageLabel, form);
  const activeMilestones = form.milestones.filter(m => m.status !== 'cancelled');
  const milestonesDone = activeMilestones.filter(m => m.status === 'completed').length;

  const saveIssues = scopingIssues(form);
  const saveBlocked = saveIssues.length > 0;

  // ── Small render helpers ──
  // Read-only field; with liveField, what the others type in it still shows live.
  const ro = (v: string, multiline = false, liveField?: string) => (<>
    <div className="form-input--bare" style={{ cursor: 'default', ...(multiline ? { whiteSpace: 'pre-wrap' as const } : {}) }}>{v || '—'}</div>
    {liveField && <LiveDraftView liveField={liveField} />}
  </>);
  // A field filled at a later stage: shown in its place, not editable yet.
  const later = (stage: string) => (
    <div className="stage-locked"><Lock size={11} /> {t('projectDetail.topics.lockedUntil', { stage: stageName(stage) })}</div>
  );
  const field = (label: string, body: ReactNode) => body == null ? null : (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {body}
    </div>
  );
  const choices = (options: string[], value: string, onPick: (v: string) => void, labelOf: (v: string) => string = (v) => v) => (
    <div className="selectable-group">
      {options.map((opt) => (
        <button key={opt} type="button" className={`selectable${value === opt ? ' active' : ''}`} disabled={!canEdit}
          onClick={() => canEdit && onPick(value === opt ? '' : opt)}>{labelOf(opt)}</button>
      ))}
    </div>
  );

  const radixSelect = (value: string, onChange: (v: string) => void, placeholder: string, items: { value: string; label: string }[]) => (
    <Select.Root value={value} onValueChange={onChange}>
      <Select.Trigger data-radix-select-trigger style={{ width: '100%', justifyContent: 'space-between' }}>
        <Select.Value placeholder={placeholder} />
        <Select.Icon data-radix-select-icon><ChevronDown className="icon-sm" /></Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content data-radix-select-content position="popper" sideOffset={4}>
          <Select.Viewport data-radix-select-viewport>
            {items.map((it) => (
              <Select.Item key={it.value} value={it.value} data-radix-select-item>
                <Select.ItemIndicator data-radix-select-item-indicator><Check className="icon-sm" /></Select.ItemIndicator>
                <Select.ItemText>{it.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );

  const domainSelect = canEdit
    ? radixSelect(form.domainId != null ? String(form.domainId) : '', (v) => setForm({ ...form, domainId: v ? Number(v) : null }),
      t('projectDetail.overview.selectDomainPlaceholder'), portfolioDomains.map(d => ({ value: String(d.id), label: d.label })))
    : ro(portfolioDomains.find(d => d.id === form.domainId)?.label || '');

  const levelSelect = canEdit
    ? radixSelect(form.levelId != null ? String(form.levelId) : '', (v) => setForm({ ...form, levelId: v ? Number(v) : null }),
      t('projectDetail.overview.selectLevelPlaceholder'), levels.map(l => ({ value: String(l.id), label: l.label })))
    : ro(levels.find(l => l.id === form.levelId)?.label || '');

  // The current accompanist stays listed even if no longer an admin.
  const accompanistChoices = [...pmoAccompanists, ...(form.pmoAccompanist && !pmoAccompanists.some(n => n.toLowerCase() === form.pmoAccompanist.toLowerCase()) ? [form.pmoAccompanist] : [])];

  // Team before Cadrage: names only (allocations are set at Cadrage).
  const intakeRoster = form.collaborators.length > 0 ? (
    <div className="detail-collabs">
      {form.collaborators.map((c, i) => (
        <div key={i} className="detail-collab"><span>{c.name}</span></div>
      ))}
    </div>
  ) : ro('');

  // ── Blocks: one card each, shared by the topic tabs and the stage's "À faire" view ──
  // `step` numbers the card as a step of the stage's work. Inside a stage view
  // (`stage`), fields that a later stage fills are left out instead of saying
  // when they open.
  type BlockOpts = { step?: StepMark; stage?: boolean };
  // "Historique (n)": the logged changes to a card's fields (see FieldHistory).
  const hist = (keys: string[]) => <FieldHistory events={projectEvents} keys={keys} />;
  const L = (o: BlockOpts, stage: string) => (o.stage ? null : later(stage));

  const problemGoalBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-problemGoal" step={o.step} actions={hist(['problem', 'goal', 'value_added'])} title={t('projectDetail.topics.problemGoal')}>
      {field(t('projectDetail.overview.problemLabel'), canEdit
        ? <BareTextarea liveField="problem" value={form.problem} onChange={(v) => setForm({ ...form, problem: v })} placeholder={t('projectDetail.overview.problemPlaceholder')} />
        : ro(form.problem, true, 'problem'))}
      {field(t('projectDetail.overview.goalLabel'), canEdit
        ? <BareTextarea liveField="goal" value={form.goal} onChange={(v) => setForm({ ...form, goal: v })} placeholder={t('projectDetail.overview.goalPlaceholder')} />
        : ro(form.goal, true, 'goal'))}
      {field(t('projectDetail.overview.valueAddedLabel'), (
        <div className="selectable-group">
          {VALUE_ADDED_OPTIONS.map((val) => (
            <button key={val} className={`selectable${form.valueAdded.includes(val) ? ' active' : ''}`} onClick={() => toggleValueAdded(val)} type="button" disabled={!canEdit}>{val}</button>
          ))}
        </div>
      ))}
    </ProjectSection>
  );

  const deliverablesBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-deliverables" step={o.step} actions={hist(['deliverables'])} title={t('projectDetail.topics.deliverables')}>
      <DeliverablesEditor deliverables={form.deliverables} onChange={(d) => setForm({ ...form, deliverables: d })} canEdit={canEdit} />
    </ProjectSection>
  );

  const scopeBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-scope" step={o.step} actions={hist(['scope_includes', 'scope_excludes'])} title={t('projectDetail.scoping.sectionLabel')}>
      {!scopingOpen ? L(o, 'Scoping') : (<>
        {field(t('projectDetail.scoping.inScopeLabel'), canEdit
          ? <BareTextarea liveField="scopeIncludes" value={form.scopeIncludes} onChange={(v) => setForm({ ...form, scopeIncludes: v })} placeholder={t('projectDetail.scoping.inScopePlaceholder')} />
          : ro(form.scopeIncludes, true, 'scopeIncludes'))}
        {field(t('projectDetail.scoping.outOfScopeLabel'), canEdit
          ? <BareTextarea liveField="scopeExcludes" value={form.scopeExcludes} onChange={(v) => setForm({ ...form, scopeExcludes: v })} placeholder={t('projectDetail.scoping.outOfScopePlaceholder')} />
          : ro(form.scopeExcludes, true, 'scopeExcludes'))}
      </>)}
    </ProjectSection>
  );

  const classificationBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-classification" step={o.step} title={t('projectDetail.topics.classification')}>
      <div className="form-grid-2">
        {field(t('projectDetail.overview.domainLabel'), domainSelect)}
        {field(t('projectDetail.overview.levelLabel'), levelSelect)}
      </div>
    </ProjectSection>
  );

  const ownershipBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-ownership" step={o.step} actions={hist(['pmo_accompanist', 'user_group'])} title={t('projectDetail.overview.ownershipSectionLabel')}>
      <div className="form-grid-2">
        {field(t('projectDetail.overview.sponsorLabel'), canEdit ? (
          <AutosuggestInput value={form.sponsor} onChange={(v) => setForm({ ...form, sponsor: v })} placeholder={t('projectDetail.overview.sponsorPlaceholder')} fetchSuggestions={async (q) => await api.getPeople(q) as any[]} onCreateNew={async (name) => { await api.createPerson(name); }} bare />
        ) : ro(form.sponsor))}
        {field(t('projectDetail.overview.pmoAccompanistLabel'), canEdit
          ? radixSelect(form.pmoAccompanist || '', (v) => setForm({ ...form, pmoAccompanist: v }), t('projectDetail.overview.pmoAccompanistPlaceholder'), accompanistChoices.map(n => ({ value: n, label: n })))
          : ro(form.pmoAccompanist))}
      </div>
      {field(t('projectDetail.overview.userGroupsLabel'), canEdit ? (
        <TagInput tags={form.userGroups} onChange={(v) => setForm({ ...form, userGroups: v })} placeholder={t('projectDetail.overview.userGroupsPlaceholder')} fetchSuggestions={async (q) => await api.getUserGroups(q) as any[]} onCreateNew={async (name) => { await api.createUserGroup(name); }} />
      ) : ro(form.userGroups.join(', ')))}
    </ProjectSection>
  );

  const teamBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-team" step={o.step}
      title={t('projectDetail.topics.team')}
      actions={<>
        {hist(['allocations'])}
        {!overviewOnly && rank >= RANK.execution && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowTeamRoles(true)} title={t('roles.teamOpenHelp')}>
            <Users size={12} /> {t('roles.teamOpen')}
          </button>
        )}
      </>}
    >
      {!scopingOpen ? (<>
        {canEdit ? <CollaboratorsSection form={form} setForm={setForm} canCreate={true} api={api} /> : intakeRoster}
        <p className="pd-note">{t('projectDetail.topics.allocationsLater', { stage: stageName('Scoping') })}</p>
      </>) : (<>
        <p className="pd-note">
          {allocWeeks
            ? t('projectDetail.scoping.allocHintWithWeeks', { hpw: HOURS_PER_WEEK, weeks: Math.round(allocWeeks) })
            : t('projectDetail.scoping.allocHintNoWeeks', { hpw: HOURS_PER_WEEK })}
        </p>
        {form.allocations.length > 0 && (
          <div className="alloc-head alloc--roles">
            <span /><span>{t('projectDetail.scoping.colPerson')}</span><ProjectRolesHeader /><span>{t('roles.precision')}</span><span>{t('projectDetail.scoping.colAllocation')}</span><span>{t('projectDetail.scoping.colHoursPerWeek')}</span><span>{t('projectDetail.scoping.colTotalHours')}</span><span />
          </div>
        )}
        {form.allocations.map((a, idx) => {
          const pct = parseFloat(a.allocationPct) || 0;
          const hpw = HOURS_PER_WEEK * pct / 100;
          const total = Math.round(hpw * allocWeeks);
          const open = expandedAlloc === idx;
          return (
            <div key={idx}>
              <div className="alloc-row alloc--roles">
                <button type="button" className="alloc-chevron" title={t('projectDetail.scoping.showOtherProjectsTitle')} onClick={() => setExpandedAlloc(open ? null : idx)}>
                  <ChevronDown className="icon-sm" style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
                </button>
                <span className="alloc-name" title={a.name}>{a.name || '—'}</span>
                {/* [Proposition: roles-projet] one of the four roles; the old free text is now the "précision" */}
                <ProjectRoleSelect value={a.projectRole} isSponsor={!!form.sponsor.trim() && a.name.trim().toLowerCase() === form.sponsor.trim().toLowerCase()} readOnly={!canEdit} onChange={(projectRole) => updateAllocation(idx, { projectRole })} />
                {canEdit ? (
                  <input className="form-input--bare" placeholder={t('roles.precisionPlaceholder')} value={a.role} onChange={(e) => updateAllocation(idx, { role: e.target.value })} />
                ) : (
                  <span className="alloc-name">{a.role || '—'}</span>
                )}
                {canEdit ? (
                  <span className="alloc-pct"><input className="form-input--bare" type="number" min={0} max={100} placeholder="0" value={a.allocationPct} onChange={(e) => updateAllocation(idx, { allocationPct: e.target.value })} /><span>%</span></span>
                ) : (
                  <span className="alloc-num">{a.allocationPct ? `${a.allocationPct}%` : '—'}</span>
                )}
                <span className="alloc-num">{hpw ? hpw.toFixed(1) : '—'}</span>
                <span className="alloc-num">{total || '—'}</span>
                {canEdit ? (
                  <button className="tag-remove" type="button" onClick={() => removeAllocation(idx)} title={t('projectDetail.scoping.removePersonTitle')}><X className="icon-sm" style={{ width: 12, height: 12 }} /></button>
                ) : <span />}
              </div>
              {open && <PersonAllocations name={a.name} projects={allProjects} excludeId={project.id} />}
            </div>
          );
        })}
        {canEdit && (
          <div className="alloc-add">
            <AutosuggestInput
              value={allocAddName}
              onChange={setAllocAddName}
              placeholder={t('projectDetail.scoping.addPersonPlaceholder')}
              fetchSuggestions={async (q) => await api.getPeople(q) as any[]}
              onCreateNew={async (name) => { await api.createPerson(name); addAllocationPerson(name); }}
              bare
            />
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => addAllocationPerson(allocAddName)} disabled={!allocAddName.trim()}><Plus size={12} /> {t('common.add')}</button>
          </div>
        )}
        <div className="alloc-total">
          <span className="alloc-total-k">{allocWeeks ? t('projectDetail.scoping.totalHoursLabel') : t('projectDetail.scoping.totalHoursSetHorizon')}</span>
          <span className="alloc-total-v">{Math.round(form.allocations.reduce((s, a) => s + (HOURS_PER_WEEK * (parseFloat(a.allocationPct) || 0) / 100) * allocWeeks, 0))} h</span>
        </div>
      </>)}
    </ProjectSection>
  );

  const calendarBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-calendar" step={o.step} actions={hist(['timeline', 'time_horizon_start', 'time_horizon_end', 'urgency', 'intervention_mode'])} title={t('projectDetail.topics.calendar')}>
      {field(t('projectDetail.overview.timelineLabel'), choices(TIMELINE_OPTIONS, form.timeline, (v) => setForm({ ...form, timeline: v })))}
      <div className="form-grid-2">
        {field(t('projectDetail.scoping.startMonth'), !scopingOpen ? L(o, 'Scoping') : canEdit
          ? <input className="form-input" type="month" value={form.timeHorizonStart} onChange={(e) => setForm({ ...form, timeHorizonStart: e.target.value })} />
          : ro(form.timeHorizonStart))}
        {field(t('projectDetail.scoping.endMonth'), !scopingOpen ? L(o, 'Scoping') : canEdit
          ? <input className="form-input" type="month" value={form.timeHorizonEnd} onChange={(e) => setForm({ ...form, timeHorizonEnd: e.target.value })} />
          : ro(form.timeHorizonEnd))}
        {field(t('projectDetail.scoping.urgencyLabel'), !scopingOpen ? L(o, 'Scoping')
          : choices(['Low', 'Medium', 'High'], form.urgency, (v) => setForm({ ...form, urgency: v }), (v) => t(URGENCY_LABEL_KEY[v])))}
        {field(t('projectDetail.scoping.interventionLabel'), !scopingOpen ? L(o, 'Scoping')
          : choices(['Weekly', 'Bi-weekly', 'Monthly'], form.interventionMode, (v) => setForm({ ...form, interventionMode: v }), (v) => t(INTERVENTION_LABEL_KEY[v])))}
      </div>
    </ProjectSection>
  );

  const budgetEffortBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-budgetEffort" step={o.step} actions={hist(['effort_size', 'cash', 'available_budget', 'cash_notes'])} title={t('projectDetail.topics.budgetEffort')}>
      {field(t('projectDetail.overview.effortSizeLabel'), choices(EFFORT_OPTIONS, form.effortSize, (v) => setForm({ ...form, effortSize: v })))}
      <div className="form-grid-2">
        {field(t('projectDetail.scoping.cashRequested'), canEdit
          ? <input className="form-input--bare" value={form.cash} onChange={(e) => setForm({ ...form, cash: e.target.value })} placeholder={t('projectDetail.overview.cashBudgetPlaceholder')} />
          : ro(form.cash))}
        {field(t('projectDetail.scoping.availableBudgetLabel'), !scopingOpen ? L(o, 'Scoping') : canEdit
          ? <input className="form-input--bare" value={form.scopeAvailableBudget} onChange={(e) => setForm({ ...form, scopeAvailableBudget: e.target.value })} placeholder={t('projectDetail.scoping.availableBudgetPlaceholder')} />
          : ro(form.scopeAvailableBudget))}
      </div>
      {field(t('projectDetail.scoping.financeNotesLabel'), !scopingOpen ? L(o, 'Scoping') : canEdit
        ? <BareTextarea liveField="cashNotes" value={form.cashNotes} onChange={(v) => setForm({ ...form, cashNotes: v })} placeholder={t('projectDetail.scoping.financeNotesPlaceholder')} />
        : ro(form.cashNotes, true, 'cashNotes'))}
    </ProjectSection>
  );

  // ── Topic: Contenu — what the project is and delivers ──
  const contenuTopic = (<>
    {problemGoalBlock()}
    {deliverablesBlock()}
    {scopeBlock()}
    {classificationBlock()}
  </>);

  // ── Topic: Équipe et moyens — who, when, with what ──
  const equipeTopic = (<>
    {ownershipBlock()}
    {teamBlock()}
    {calendarBlock()}
    {budgetEffortBlock()}
  </>);

  // ── Topic: Stratégie — préqualification assessment and the decisions taken ──
  // (The two assessment panels draw their own section header, styled like ProjectSection.)
  const prequalVerdictText = form.prequalVerdict === 'advance' ? t('projectDetail.prequalDecision.verdictAdvance')
    : form.prequalVerdict === 'defer' || currentStageLabel === 'Deferred' ? t('projectDetail.prequalDecision.verdictDeferred')
    : rank >= RANK.scoping ? t('projectDetail.prequalDecision.verdictAdvance')
    : currentStageLabel === 'Préqualification' ? t('projectDetail.synth.pendingInBar') : '';
  // The two assessment panels draw their own section header, styled as the card
  // header; a step number goes in front of it through --step-label.
  const panelCard = (id: string, o: BlockOpts, body: ReactNode, histKeys: string[]) => (
    <section id={id} className={`pd-card pd-card--panel${o.step ? ` has-step step-${o.step.done === true ? 'done' : o.step.done === false ? 'todo' : 'opt'}` : ''}`}
      style={o.step ? { ['--step-label' as string]: `"${o.step.done === true ? '✓' : o.step.n}"` } : undefined}>
      {body}
      {fieldChanges(projectEvents, histKeys).length > 0 && <div className="pd-panel-history">{hist(histKeys)}</div>}
    </section>
  );
  const strategicBlock = (o: BlockOpts = {}) => panelCard('blk-strategic', o,
    <StrategicPanel form={form} setForm={setForm} projectId={project.id} canEdit={canEdit} />, ['rcajo']);
  const sequencingBlock = (o: BlockOpts = {}) => panelCard('blk-sequencing', o,
    <SequencingPanel form={form} setForm={setForm} projectId={project.id} canEdit={canEdit} />, ['sequencing']);
  const decisionsBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-decisions" step={o.step} actions={hist(['contacted_sponsor', 'prequal_notes'])} title={t('projectDetail.synth.decisions')}>
      <label className="pq-checkbox">
        <input type="checkbox" checked={form.prequalContactedSponsor} disabled={!canEdit}
          onChange={(e) => setForm({ ...form, prequalContactedSponsor: e.target.checked })} />
        <span>{t('projectDetail.prequalDecision.contactedSponsor')}</span>
      </label>
      <div className="form-grid-2" style={{ marginTop: 'var(--s16)' }}>
        {field(t('projectDetail.stage.prequalification'), ro(prequalVerdictText))}
        {field(t('projectDetail.stage.qualification'), project.qualified_at ? (
          <div className="form-input--bare" style={{ cursor: 'default' }}>
            {project.qualification_outcome === 'backlog' ? t('projectDetail.qualification.badgeBacklogged') : t('projectDetail.qualification.badgeQualified')}
            {' '}<span className="text-ink-3">· {new Date(project.qualified_at).toLocaleDateString()}</span>
          </div>
        ) : rank < RANK.qualification ? L(o, 'Qualification') : ro(t('projectDetail.qualification.pending')))}
      </div>
    </ProjectSection>
  );
  const strategieTopic = (<>
    {strategicBlock()}
    {sequencingBlock()}
    {decisionsBlock()}
  </>);

  // ── Topic: Plan et exécution — milestones, then their follow-up ──
  // Once the project runs, the tab opens on the follow-up; the plan editor is
  // one click away. The switch sits at the top right of the tab in both views.
  const inExecution = rank >= RANK.execution;
  const planSwitch = inExecution ? (
    <div className="selectable-group pd-view-switch" role="tablist">
      <button type="button" role="tab" aria-selected={planView === 'suivi'} className={`selectable${planView === 'suivi' ? ' active' : ''}`} onClick={() => setPlanView('suivi')}>{t('projectDetail.topics.followUp')}</button>
      <button type="button" role="tab" aria-selected={planView === 'plan'} className={`selectable${planView === 'plan' ? ' active' : ''}`} onClick={() => setPlanView('plan')}>{t('projectDetail.topics.editPlan')}</button>
    </div>
  ) : undefined;
  const deliverableTitles = form.deliverables.map(d => d.title.trim()).filter(Boolean);
  // What the milestones must deliver — edited in Contenu, recalled here.
  const deliverablesToCoverBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-tocover" step={o.step} title={t('projectDetail.topics.deliverablesToCover')} actions={o.step ? undefined : planSwitch}>
      {deliverableTitles.length === 0 ? <p className="pd-note">{t('projectDetail.synth.noDeliverables')}</p> : (
        <ol className="pd-plain-list">
          {deliverableTitles.map((title, i) => <li key={i}>{title}</li>)}
        </ol>
      )}
    </ProjectSection>
  );
  const milestonesBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-milestones" step={o.step} title={t('projectDetail.planning.milestonesLabel')}>
      <MilestonesEditor
        projectId={projectId}
        milestones={form.milestones}
        onChange={(ms) => setForm({ ...form, milestones: ms })}
        collaborators={form.collaborators.map(c => c.name).filter(Boolean)}
        deliverables={form.deliverables}
        canEdit={canEdit}
      />
    </ProjectSection>
  );
  const planEditorView = (<>
    {deliverablesToCoverBlock()}
    {milestonesBlock()}
  </>);
  // The project's status (from its milestones) is in the masthead; the
  // Suivi / Modifier le plan switch heads the first card of either view.
  const followUpView = (
    <ProjectSection title={t('projectDetail.topics.milestonesAndTasks')} actions={planSwitch} flush>
      <VueJalonsTaches projectId={project.id} onChanged={refreshAfterExecutionView} onDocumentsChanged={refreshAfterDocuments} reloadKey={executionKey}
        {...(canEdit ? { onAddMilestone: () => setAddingMilestone(newMilestone()) } : {})} />
    </ProjectSection>
  );
  const planTopic = inExecution && planView === 'suivi' ? followUpView : planEditorView;

  // ── Topic: Clôture — outcome and handover notes ──
  const outcomeBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-outcome" step={o.step} title={t('projectDetail.handover.outcomeSectionLabel')}>
      <p className="pd-note">{t('projectDetail.handover.outcomeHint')}</p>
      {(() => {
        const caps = form.rcajo?.capabilities ?? [];
        const standalone = form.rcajo?.resources ?? [];
        if (!caps.some(c => c.resources.length) && standalone.length === 0) {
          return <p className="pd-note">{t('projectDetail.handover.noImprovements')}</p>;
        }
        const renderRes = (r: { resource_title: string; current_score: number | null; target_score: number; achieved_score?: number | null }, capIdx: number | null, resIdx: number) => (
          <div key={resIdx} className="ho-res">
            <span className="ho-res-name" title={r.resource_title}>{r.resource_title}</span>
            <span className="ho-res-target">{r.current_score ?? '—'} <span className="pq-mat-arrow">→</span> {r.target_score}</span>
            <span className="ho-res-ach">
              <span className="text-sm text-ink-3">{t('projectDetail.handover.achievedLabel')}</span>
              {canEdit ? (
                <input className="form-input--bare ho-ach-input" type="number" step="0.5" min="0" max="5"
                  value={r.achieved_score ?? r.target_score}
                  onChange={(e) => setAchieved(capIdx, resIdx, e.target.value === '' ? null : Number(e.target.value))} />
              ) : (
                <strong>{r.achieved_score ?? r.target_score}</strong>
              )}
            </span>
          </div>
        );
        return (
          <div className="flex flex-col gap-12">
            {caps.map((c, ci) => c.resources.length > 0 && (
              <div key={ci} className="ho-cap">
                <div className="ho-cap-title">{c.capability_title}</div>
                {c.resources.map((r, ri) => renderRes(r, ci, ri))}
              </div>
            ))}
            {standalone.length > 0 && (
              <div className="ho-cap">
                <div className="ho-cap-title">{t('projectDetail.handover.resourcesTitle')}</div>
                {standalone.map((r, ri) => renderRes(r, null, ri))}
              </div>
            )}
          </div>
        );
      })()}
    </ProjectSection>
  );
  const handoverNotesBlock = (o: BlockOpts = {}) => (
    <ProjectSection id="blk-handover" step={o.step} actions={hist(['handover_notes'])} title={t('projectDetail.handover.notesLabel')}>
      {canEdit ? <BareTextarea liveField="handoverNotes" value={form.handoverNotes} onChange={(v) => setForm({ ...form, handoverNotes: v })} placeholder={t('projectDetail.handover.notesPlaceholder')} /> : ro(form.handoverNotes, true, 'handoverNotes')}
    </ProjectSection>
  );
  const clotureTopic = (<>
    {outcomeBlock()}
    {handoverNotesBlock()}
  </>);

  // ── Navigation: a tab, then the card asked for (or the tab's top) ──
  // The new tab renders first; the card is then scrolled to the top of the window.
  const scrollToCard = (anchorId?: string) => {
    let tries = 0;
    const go = () => {
      const el = anchorId ? document.getElementById(anchorId) : document.querySelector<HTMLElement>('.pd-tabs');
      if (el) scrollToTop(el);
      else if (++tries < 20) setTimeout(go, 50);
    };
    setTimeout(go, 40);
  };
  const goTo = (topic: ProjectTopic, anchorId?: string) => {
    if (topic !== 'etape') setViewedStage(null);
    setActiveTopic(topic);
    if (topic === 'plan') setPlanView('suivi');
    scrollToCard(anchorId);
  };
  // A stage clicked in the header: its "À faire" view. Execution works in
  // Plan et exécution, a closed project in Clôture.
  const viewStage = (label: string) => {
    if (label === 'Execution') { goTo('plan'); return; }
    if (label === 'Closed') { goTo('cloture'); return; }
    setViewedStage(label === currentStageLabel ? null : label);
    setActiveTopic('etape');
    scrollToCard();
  };
  // A checklist item in the header band: the card where it is filled, in the current stage's view.
  const CHECK_TO_BLOCK: Record<string, string> = {
    problem: 'blk-problemGoal', goal: 'blk-problemGoal', valueAdded: 'blk-problemGoal',
    deliverables: 'blk-deliverables', effortEstimate: 'blk-budgetEffort', sponsor: 'blk-ownership',
    verdictAdvance: 'blk-decision',
    inScope: 'blk-scope', outOfScope: 'blk-scope',
    urgency: 'blk-calendar', interventionMode: 'blk-calendar', timeHorizon: 'blk-calendar',
    atLeastOneAllocation: 'blk-team', atLeastOneMilestone: 'blk-milestones',
  };
  const jumpToItem = (item: StageChecklistItem) => {
    if (item.key === 'milestonesDone') { goTo('plan'); return; }
    if (item.key === 'name') { window.scrollTo({ top: 0, behavior: 'smooth' }); document.querySelector<HTMLInputElement>('input.pd-title')?.focus(); return; }
    if ((STAGES_WITH_VIEW as readonly string[]).includes(currentStageLabel)) goTo('etape', CHECK_TO_BLOCK[item.key]);
    else goTo(item.topic, CHECK_TO_BLOCK[item.key]);
  };

  const stageNextProps = {
    currentLabel: currentStageLabel, rank, checklist,
    milestonesDone, milestonesTotal: activeMilestones.length, canEdit,
    onJumpItem: jumpToItem, onViewStage: viewStage,
    onAdvance: setAdvanceTarget, onDecide: setDecideTarget, onClose: () => setConfirmClose(true),
  };

  // ── The stage's "À faire" view: what this stage asks for, as numbered steps ──
  type StageStep = { id: string; title: string; done: boolean | null; render: (o: BlockOpts) => ReactNode };
  const rcajo = form.rcajo;
  const strategicDone = !!rcajo && (rcajo.is_strategic || !!rcajo.rationale?.trim() || rcajo.capabilities.length > 0 || (rcajo.resources?.length ?? 0) > 0);
  const step = (id: string, title: string, done: boolean | null, render: (o: BlockOpts) => ReactNode): StageStep => ({ id, title, done, render });
  // An optional step turns green (✓) once something is filled in, and reads "facultatif" while empty.
  const opt = (filled: boolean): boolean | null => (filled ? true : null);
  const sq = form.sequencing;
  const STAGE_STEPS: Record<string, StageStep[]> = {
    Inbox: [
      step('blk-problemGoal', t('projectDetail.topics.problemGoal'), !!form.problem.trim() && !!form.goal.trim() && form.valueAdded.length > 0, problemGoalBlock),
      step('blk-deliverables', t('projectDetail.topics.deliverables'), form.deliverables.length > 0, deliverablesBlock),
      step('blk-ownership', t('projectDetail.overview.ownershipSectionLabel'), !!form.sponsor.trim(), ownershipBlock),
      step('blk-team', t('projectDetail.topics.team'), opt(form.collaborators.length > 0), teamBlock),
      step('blk-budgetEffort', t('projectDetail.topics.budgetEffort'), !!form.effortSize, budgetEffortBlock),
      step('blk-calendar', t('projectDetail.topics.calendar'), opt(!!form.timeline), calendarBlock),
      step('blk-classification', t('projectDetail.topics.classification'), opt(form.domainId != null || form.levelId != null), classificationBlock),
    ],
    'Préqualification': [
      step('blk-strategic', t('projectDetail.stageView.strategic'), strategicDone, strategicBlock),
      step('blk-sequencing', t('projectDetail.stageView.sequencing'), opt(!!sq && (sq.depends_on_projects.length + sq.enables_projects.length + sq.depends_on_resources.length + sq.enables_resources.length > 0)), sequencingBlock),
    ],
    Scoping: [
      step('blk-scope', t('projectDetail.scoping.sectionLabel'), !!form.scopeIncludes.trim() && !!form.scopeExcludes.trim(), scopeBlock),
      step('blk-calendar', t('projectDetail.topics.calendar'), !!form.urgency && !!form.interventionMode && !!form.timeHorizonStart && !!form.timeHorizonEnd, calendarBlock),
      step('blk-team', t('projectDetail.topics.team'), form.allocations.length > 0, teamBlock),
      step('blk-budgetEffort', t('projectDetail.topics.budgetEffort'), opt(!!form.scopeAvailableBudget.trim() || !!form.cashNotes.trim() || form.cashRequired), budgetEffortBlock),
    ],
    Qualification: [
      step('blk-decisions', t('projectDetail.synth.decisions'), null, decisionsBlock),
    ],
    Planning: [
      step('blk-tocover', t('projectDetail.topics.deliverablesToCover'), null, deliverablesToCoverBlock),
      step('blk-milestones', t('projectDetail.planning.milestonesLabel'), form.milestones.some(m => m.status !== 'cancelled'), milestonesBlock),
    ],
    Handover: [
      step('blk-outcome', t('projectDetail.handover.outcomeSectionLabel'), null, outcomeBlock),
      step('blk-handover', t('projectDetail.handover.notesLabel'), !!form.handoverNotes.trim() || null, handoverNotesBlock),
    ],
  };
  const isSideState = currentStageLabel === 'Deferred' || currentStageLabel === 'Backlog';
  const hasOwnView = (STAGES_WITH_VIEW as readonly string[]).includes(currentStageLabel);
  const shownStage = viewedStage ?? (hasOwnView ? currentStageLabel : null);
  const shownIdx = shownStage ? (LIFECYCLE_STAGES as readonly string[]).indexOf(shownStage) : -1;
  const shownWhen: 'past' | 'current' | 'future' = shownStage === currentStageLabel ? 'current'
    : shownIdx < rank || (isSideState && shownIdx <= rank) || currentStageLabel === 'Closed' ? 'past' : 'future';

  const stageView = (() => {
    if (!shownStage) return { aside: null, body: null };
    const steps = STAGE_STEPS[shownStage] ?? [];
    const name = stageName(shownStage);
    // The current stage ends with its decision: a numbered last step.
    const decision = shownWhen === 'current' ? {
      id: 'blk-decision',
      title: currentStageLabel === 'Préqualification' ? t('projectDetail.stageView.decision') : t('projectDetail.stageView.next'),
    } : null;
    const all = [...steps.map(s => ({ id: s.id, title: s.title, done: s.done })), ...(decision ? [{ ...decision, done: false as boolean | null }] : [])];
    const intro = (
      <ProjectSection
        title={shownWhen === 'current' ? t('projectDetail.stageView.titleCurrent', { stage: name })
          : shownWhen === 'past' ? t('projectDetail.stageView.titlePast', { stage: name })
          : t('projectDetail.stageView.titleFuture', { stage: name })}
        actions={viewedStage && hasOwnView ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => viewStage(currentStageLabel)}>
            {t('projectDetail.stageView.backToCurrent', { stage: stageName(currentStageLabel) })}
          </button>
        ) : undefined}>
        <p className="pd-stage-help">{t(`projectDetail.stageView.help.${shownStage}`)}</p>
        {shownWhen === 'past' && <p className="pd-note">{t('projectDetail.stageView.pastNote')}</p>}
        {shownWhen === 'future' && <p className="pd-note">{t('projectDetail.stageView.futureNote', { stage: stageName(currentStageLabel) })}</p>}
        <ol className="pd-steps">
          {all.map((s, i) => (
            <li key={s.id}>
              {shownWhen === 'future' ? (
                <span className="pd-steps-item"><StepBadge step={{ n: i + 1, done: null }} /><span>{s.title}</span></span>
              ) : (
                <button type="button" className="pd-steps-item" onClick={() => scrollToCard(s.id)}>
                  <StepBadge step={{ n: i + 1, done: s.done }} />
                  <span>{s.title}</span>
                  {s.done === null && s.id !== 'blk-decision' && <span className="pd-steps-opt">{t('projectDetail.stageView.optional')}</span>}
                </button>
              )}
            </li>
          ))}
        </ol>
      </ProjectSection>
    );
    // Current and past stages: the step list sits at the top of the right
    // column on wide screens (beside the cards it points to), and at the top of
    // the page when the right column drops below the content.
    if (shownWhen === 'future') return { aside: null, body: intro };
    return { aside: intro, body: (<>
      <div className="pd-only-narrow">{intro}</div>
      {steps.map((s, i) => <Fragment key={s.id}>{s.render({ step: { n: i + 1, done: s.done }, stage: true })}</Fragment>)}
      {decision && (
        <ProjectSection id="blk-decision" step={{ n: steps.length + 1, done: false }} title={decision.title}>
          {currentStageLabel === 'Préqualification' && (
            <label className="pq-checkbox pd-decision-check">
              <input type="checkbox" checked={form.prequalContactedSponsor} disabled={!canEdit}
                onChange={(e) => setForm({ ...form, prequalContactedSponsor: e.target.checked })} />
              <span>{t('projectDetail.prequalDecision.contactedSponsor')}</span>
            </label>
          )}
          <StageNext {...stageNextProps} />
        </ProjectSection>
      )}
    </>) };
  })();

  // ── Project settings (dialog from the header): visibility, blocked mode, delete ──
  const settingsDialog = canEdit && showSettings && (
    <div className="modal-overlay" onClick={() => setShowSettings(false)}>
      <div className="modal pd-settings" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
        <div className="modal-header">
          <h3 className="modal-title">{t('projectDetail.topics.settings')}</h3>
          <button className="close-btn" onClick={() => setShowSettings(false)}><X size={16} /></button>
        </div>
        <div className="modal-body">
          {isAdmin && (
            <SettingsBlock title={t('projectDetail.visibility')}>
              <div className="selectable-group">
                <button type="button" className={`selectable${!isPrivate ? ' active' : ''}`}
                  onClick={async () => { await api.setProjectPrivacy(project.id, false); setIsPrivate(false); }}>
                  {t('projectDetail.visibilityPublic')}
                </button>
                <button type="button" className={`selectable${isPrivate ? ' active' : ''}`}
                  onClick={async () => { await api.setProjectPrivacy(project.id, true); setIsPrivate(true); }}>
                  {t('projectDetail.visibilityPrivate')}
                </button>
              </div>
              {isPrivate && <p className="pd-note" style={{ marginTop: 'var(--s8)' }}>{t('projectDetail.visibilityPrivateHelp')}</p>}
            </SettingsBlock>
          )}
          {/* Set the stage by hand (PMO): e.g. to correct a wrong move. */}
          {isAdmin && (
            <SettingsBlock title={t('projectDetail.stageSetTitle')}>
              <div className="selectable-group">
                {pipelineColumns.filter(c => (LIFECYCLE_STAGES as readonly string[]).includes(c.label) || c.label === 'Deferred' || c.label === 'Backlog')
                  .sort((x, y) => x.sort_order - y.sort_order)
                  .map(c => (
                    <button key={c.id} type="button" className={`selectable${c.label === currentStageLabel ? ' active' : ''}`}
                      onClick={() => { if (c.label !== currentStageLabel) { setShowSettings(false); setStatusTarget(c.label); } }}>
                      {stageName(c.label)}
                    </button>
                  ))}
              </div>
              <p className="pd-note" style={{ marginTop: 'var(--s8)' }}>{t('projectDetail.stageSetHelp')}</p>
            </SettingsBlock>
          )}
          {/* Blocked mode (issue #41) */}
          <SettingsBlock
            title={t('projectDetail.blockedMode')}
            actions={isBlocked ? (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={async () => { await api.setProjectBlocked(project.id, false, null); setIsBlocked(false); setBlockedReason(''); }}>
                {t('projectDetail.unblock')}
              </button>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm" disabled={!blockedReasonDraft.trim()}
                onClick={async () => {
                  const reason = blockedReasonDraft.trim();
                  await api.setProjectBlocked(project.id, true, reason);
                  setIsBlocked(true);
                  setBlockedReason(reason);
                  setBlockedReasonDraft('');
                }}>
                {t('projectDetail.markBlocked')}
              </button>
            )}
          >
            {field(t('projectDetail.blockedReasonLabel'), isBlocked ? (
              <BareTextarea
                value={blockedReason}
                onChange={setBlockedReason}
                onBlur={async () => {
                  const trimmed = blockedReason.trim();
                  if (!trimmed || trimmed === (project.blocked_reason || '')) return;
                  await api.setProjectBlocked(project.id, true, trimmed);
                  loadData();
                }}
                placeholder={t('projectDetail.blockedReasonPlaceholder')}
              />
            ) : (
              <BareTextarea value={blockedReasonDraft} onChange={setBlockedReasonDraft} placeholder={t('projectDetail.blockedReasonPlaceholder')} />
            ))}
          </SettingsBlock>
          {isAdmin && (
            <SettingsBlock
              title={t('projectDetail.deleteInitiativeTitle')}
              actions={<button type="button" className="btn btn-danger btn-sm" onClick={() => { setShowSettings(false); handleDelete(); }}>{t('common.delete')}</button>}
            >
              <p className="pd-note">{t('projectDetail.deleteInitiativeHelp')}</p>
            </SettingsBlock>
          )}
        </div>
      </div>
    </div>
  );

  const TOPIC_BODY: Record<ProjectTopic, ReactNode> = {
    etape: stageView.body,
    apercu: (<>
      <ProjectSynthesis
        form={form}
        project={project}
        currentLabel={currentStageLabel}
        rank={rank}
        domainLabel={portfolioDomains.find(d => d.id === form.domainId)?.label ?? null}
        levelLabel={levels.find(l => l.id === form.levelId)?.label ?? null}
        canEdit={canEdit}
        onJump={goTo}
      />
      {/* [Proposition: documents] Before Execution, the full register lives here
          (once the project runs, it is at the bottom of the follow-up view). For everyone
          who opens the project in full (the documents routes check project access). */}
      {!overviewOnly && !hasFollowUp && (
        <section className="pd-card pd-card--docs">
          <ProjectDocumentsRegister projectId={project.id} reloadKey={docsKey} onChanged={refreshAfterDocuments} />
        </section>
      )}
    </>),
    contenu: contenuTopic,
    equipe: equipeTopic,
    strategie: strategieTopic,
    plan: planTopic,
    cloture: clotureTopic,
  };

  const lockedTopic = (topic: ProjectTopic) => (
    <section className="pd-card">
      <div className="pd-card-body pd-locked">
        <Lock size={18} />
        <div className="pd-locked-title">{t('projectDetail.topics.opensAt', { stage: stageName(UNLOCK_STAGE[topic]) })}</div>
        <div className="pd-locked-desc">{t(`projectDetail.topics.lockedDesc.${topic}`)}</div>
      </div>
    </section>
  );

  // The footer's Save: on every editable topic; on the read-only Aperçu only
  // when there are unsaved edits from another tab.
  const showFooter = canEdit && topicOpen(currentTopic) && (currentTopic !== 'apercu' || isDirty);

  return (
    // Live fields: the others on this project see what is typed in its phases' fields.
    <LiveScope prefix={`project:${project.id}`}>
    <div className="page project-page">
      {/* Others' pointers, over the milestones, tasks, plan cards and timeline entries (data-live-anchor). */}
      <LiveCursors people={livePeople} />
      {isChangeRequestMode && (
        <div className="direction-banner">
          {t('projectDetail.changeRequestBanner')}
        </div>
      )}
      {submitFeedback && (
        <div className={`direction-banner ${submitFeedbackOk ? 'direction-banner--success' : 'direction-banner--error'}`}>
          {submitFeedback}
        </div>
      )}
      {addingMilestone && (
        <div className="modal-overlay" onClick={() => setAddingMilestone(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, width: '100%' }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('milestones.addMilestone')}</h3>
              <button className="close-btn" onClick={() => setAddingMilestone(null)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <MilestoneCard
                m={addingMilestone}
                index={form.milestones.length}
                projectId={projectId}
                collaborators={form.collaborators.map(c => c.name).filter(Boolean)}
                deliverables={form.deliverables}
                onChange={setAddingMilestone}
              />
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAddingMilestone(null)}>{t('common.cancel')}</button>
              <button className="btn btn-accent" disabled={!addingMilestone.name.trim()} onClick={saveAddedMilestone}>
                <Plus size={14} /> {isChangeRequestMode ? t('projectDetail.requestChange') : t('milestones.addMilestone')}
              </button>
            </div>
          </div>
        </div>
      )}
      {remoteChange && (
        <div className="direction-banner">
          {remoteChange.actor ? t('live.changedBy', { name: remoteChange.actor }) : t('live.changedBySomeone')}{' '}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setRemoteChange(null); loadData(); }}>
            {t('live.reload')}
          </button>
        </div>
      )}

      <button className="form-back pd-back" onClick={goBack}>
        <ArrowLeft className="icon-sm" /> {t('common.back')}
      </button>

      {/* ── Masthead: who the project is, where it stands, what comes next ── */}
      <header className="pd-masthead initiative-form">
        <div className="pd-masthead-top">
          <div className="pd-masthead-id">
            <div className="pd-eyebrow">
              <span>{t('projectDetail.initiativeLabel')}</span>
              <span className="badge">{projectDisplayCode(project)}</span>
              {form.rcajo?.is_strategic && <span className="badge badge-ok">{t('prequal.strategic')}</span>}
              {isBlocked && <span className="badge badge-err" title={blockedReason || undefined}>{t('projectDetail.blockedBadge')}</span>}
              {/* In Execution: the project's status from its milestones (late, ending soon, on time). */}
              {rank === RANK.execution && (() => {
                const h = milestoneHealth({ milestones: form.milestones });
                return <span className={`badge ${HEALTH_BADGE_CLASS[h.level]}`} title={h.milestone}>{t(HEALTH_TITLE_KEY[h.level])}{h.milestone ? ` · ${h.milestone}` : ''}</span>;
              })()}
            </div>
            {canEdit ? (
              <input
                className="form-input--bare bare-title pd-title"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('projectDetail.initiativeNamePlaceholder')}
                title={form.name}
              />
            ) : (
              <div className="form-input--bare bare-title pd-title" title={form.name}>{form.name}</div>
            )}
          </div>
          <div className="pd-masthead-tools">
            <PresenceAvatars people={livePeople} />
            {/* Collaborator mode: for the people concerned by the project (not an overview-only visitor). */}
            {!overviewOnly && <CollabButton />}
            <ShareProject projectId={projectId} tab={currentTopic} />
            <button type="button" className="btn-icon"
              title={stageLinkCopied ? t('common.copied') : t('projectDetail.copyStageLink')}
              onClick={copyStageLink}>
              {stageLinkCopied ? <Check size={14} /> : <LinkIcon size={14} />}
            </button>
            {canEdit && (
              <button type="button" className="btn-icon" title={t('projectDetail.topics.settings')} onClick={() => setShowSettings(true)}>
                <Settings size={14} />
              </button>
            )}
          </div>
        </div>

        {/* The process: stage, what the next one needs, the one advance button. */}
        {!overviewOnly && (
          <ProjectStageBar
            pipelineColumns={pipelineColumns}
            viewedLabel={currentTopic === 'etape' ? shownStage : null}
            {...stageNextProps}
          />
        )}
      </header>

      {/* ── The content, by topic ── */}
      <div className="tabs pd-tabs initiative-form" role="tablist">
        {topics.map((topic) => {
          const open = topicOpen(topic);
          return (
            <button
              key={topic}
              role="tab"
              aria-selected={currentTopic === topic}
              className={`tab${currentTopic === topic ? ' active' : ''}${open ? '' : ' is-locked'}${topic === 'etape' ? ' tab--stage' : ''}`}
              onClick={() => { if (topic !== 'etape') setViewedStage(null); setActiveTopic(topic); }}
              type="button"
              title={open ? undefined : t('projectDetail.topics.opensAt', { stage: stageName(UNLOCK_STAGE[topic]) })}
            >
              {!open && <Lock className="tab-lock" />}
              {topic === 'etape' && shownStage
                ? (shownWhen === 'current' ? t('projectDetail.topic.etapeCurrent', { stage: stageName(shownStage) }) : t('projectDetail.topic.etapeOther', { stage: stageName(shownStage) }))
                : t(`projectDetail.topic.${topic}`)}
            </button>
          );
        })}
      </div>

      <div className="pd-layout">
        <main className="pd-main initiative-form">
          {topicOpen(currentTopic) ? TOPIC_BODY[currentTopic] : lockedTopic(currentTopic)}

          {showFooter && (
            <div className={`pd-savebar${isDirty ? ' is-dirty' : ''}`}>
              <span className="pd-savebar-state">
                {saveBlocked
                  ? <span className="pd-savebar-err">{t('projectDetail.scoping.saveBlockedHint', { issues: saveIssues.join(' · ') })}</span>
                  : isDirty ? t('projectDetail.topics.unsaved') : t('projectDetail.topics.allSaved')}
              </span>
              <button className="btn btn-ghost" onClick={goBack}>{t('common.cancel')}</button>
              <button
                className="btn btn-accent"
                onClick={handleSave}
                disabled={!form.name.trim() || saveBlocked || !isDirty}
                title={saveBlocked ? saveIssues.join(' · ') : undefined}
              >
                {isChangeRequestMode ? t('projectDetail.requestChange') : t('projectDetail.saveChanges')}
              </button>
            </div>
          )}
        </main>

        <aside className="pd-side">
          {overviewOnly ? (
            <section className="pd-card"><div className="pd-card-body pd-note">{t('projectDetail.overviewOnly')}</div></section>
          ) : (<>
            {/* The project's own task board (Organisation tâches, filtered on this project). */}
            <a className="btn btn-accent project-board-link" href={`#tasks?project=${project.id}`} title={t('projectDetail.tasksPanel.boardOpenHelp')}>
              <LayoutGrid size={14} /> {t('projectDetail.boardButton')}
            </a>
            {currentTopic === 'etape' && stageView.aside && <div className="pd-only-wide">{stageView.aside}</div>}
            {/* [Proposition: vue-jalons-taches / documents] For the proposals' reviewers, the side
                panel is lighter: no task table (tasks live in the follow-up view of Plan et
                exécution) and the documents reduced to a summary that opens the full register.
                The project history always stays, composer included. Each block is a card;
                their own headers are styled as card headers (pd-side-card). */}
            {!canSeeProposals && (
              <div className="pd-card pd-side-card">
                <ProjectTasksPanel projectId={project.id} projectName={project.name} projectCode={project.project_code} allProjects={allProjects} />
              </div>
            )}
            {/* Documents: the summary with "Joindre un fichier" / "Ajouter un lien", for everyone. */}
            <div className="pd-card pd-side-card">
              <DocumentsSummary projectId={project.id} onShowAll={showAllDocuments} canAdd
                reloadKey={docsKey} onChanged={refreshAfterDocuments} />
            </div>
            {/* The comment composer stays for everyone: posting a comment with the paperclip
                attaches a file at ANY stage (for the proposals' reviewers, the Documents card
                above has its own "Joindre un fichier" too). Reviewers only get the compact rendering. */}
            <div className="pd-card pd-side-card">
              <ProjectTimeline projectId={project.id} reloadKey={timelineKey} onChanged={refreshAfterTimeline} {...(canSeeProposals ? { compact: true } : {})} />
            </div>
          </>)}
        </aside>
      </div>

      {settingsDialog}

      {showTeamRoles && <ProjectTeamRolesPanel project={project} onClose={() => setShowTeamRoles(false)} />}

      <ConfirmModal
        open={confirmDelete}
        title={t('projectDetail.deleteInitiativeTitle')}
        message={t('projectDetail.deleteInitiativeMessage')}
        confirmLabel={t('common.delete')}
        destructive
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(false)}
      />

      <ConfirmModal
        open={decideTarget === 'advance'}
        title={t('projectDetail.prequalDecision.advanceTitle')}
        message={t('projectDetail.prequalDecision.advanceModalMessage')}
        confirmLabel={t('projectDetail.advance')}
        onConfirm={() => applyDecision('advance')}
        onCancel={() => setDecideTarget(null)}
      />
      <ConfirmModal
        open={decideTarget === 'defer'}
        title={t('projectDetail.prequalDecision.deferTitle')}
        message={t('projectDetail.prequalDecision.deferModalMessage')}
        confirmLabel={t('projectDetail.prequalDecision.deferConfirmLabel')}
        onConfirm={() => applyDecision('defer')}
        onCancel={() => setDecideTarget(null)}
      />

      <ConfirmModal
        open={advanceTarget != null}
        title={t('projectDetail.advanceTitleTo', { target: advanceTarget ? stageName(advanceTarget) : '' })}
        message={advanceMessage(t, advanceTarget, stageName)}
        confirmLabel={t('projectDetail.advance')}
        onConfirm={() => advanceTarget && doAdvanceTo(advanceTarget)}
        onCancel={() => setAdvanceTarget(null)}
      />

      <ConfirmModal
        open={confirmClose}
        title={t('projectDetail.recordCloseTitle')}
        message={t('projectDetail.recordCloseMessage')}
        confirmLabel={t('projectDetail.recordAndClose')}
        onConfirm={recordAndClose}
        onCancel={() => setConfirmClose(false)}
      />

      {(() => {
        const tgtIdx = statusTarget ? PHASE_ORDER.indexOf(statusTarget) : -1;
        const curIdx = PHASE_ORDER.indexOf(currentStageLabel);
        const isRevert = tgtIdx >= 0 && curIdx >= 0 && tgtIdx < curIdx;
        const target = statusTarget ? stageName(statusTarget) : '';
        return (
          <ConfirmModal
            open={statusTarget != null}
            title={t(isRevert ? 'projectDetail.revertTitleTo' : 'projectDetail.advanceTitleTo', { target })}
            message={isRevert
              ? t('projectDetail.revertMessage', { target })
              : t('projectDetail.advanceForwardMessage', { target })}
            confirmLabel={isRevert ? t('projectDetail.revert') : t('projectDetail.advance')}
            onConfirm={() => { if (statusTarget) doAdvanceTo(statusTarget); setStatusTarget(null); }}
            onCancel={() => setStatusTarget(null)}
          />
        );
      })()}
    </div>
    </LiveScope>
  );
}
